import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Button, Row, Col } from 'react-bootstrap';
import { useTitle } from '../../context/TitleContext';
import packRepository from '../../services/repositories/pack.repository';
import productRepository from '../../services/repositories/product.repository';
import '../../styles/products.scss';

const PackEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [packData, setPackData] = useState({
    name: '',
    type: '',
    products: [],
    price: '',
    status: 'active',
    quantity: '',
    description: ''
  });

  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setTitle('Modifier le Pack');
  }, [setTitle]);

  useEffect(() => {
    fetchPackData();
    fetchProducts();
  }, [id]);

  const fetchPackData = async () => {
    try {
      const response = await packRepository.getById(id);
      const pack = response.pack || response;
      setPackData({
        name: pack.name || '',
        type: pack.type || '',
        products: pack.products || [],
        price: pack.price || '',
        status: pack.status || 'active',
        quantity: pack.quantity || '',
        description: pack.description || ''
      });
    } catch (err) {
      setError('Erreur lors du chargement du pack');
      console.error('Error fetching pack:', err);
    } finally {
      setFetching(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productRepository.getAll();
      let products = [];
      if (response) {
        if (Array.isArray(response)) {
          products = response;
        } else if (response.data && Array.isArray(response.data)) {
          products = response.data;
        } else if (typeof response === 'object') {
          products = [response];
        }
      }
      setAvailableProducts(products);
    } catch (err) {
      console.error('Error fetching products:', err);
      setAvailableProducts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPackData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductAdd = (productId) => {
    const product = availableProducts.find(p => p.id === parseInt(productId));
    if (product && !packData.products.find(p => p.id === product.id)) {
      setPackData(prev => ({
        ...prev,
        products: [...prev.products, { id: product.id, quantity: 1 }]
      }));
    }
  };

  const handleProductSelect = (productId) => {
    handleProductAdd(productId);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const getFilteredProducts = () => {
    if (!searchTerm.trim()) {
      return availableProducts.filter(product => 
        !packData.products.find(p => parseInt(p.id) === parseInt(product.id))
      );
    }
    return availableProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const notSelected = !packData.products.find(p => parseInt(p.id) === parseInt(product.id));
      return matchesSearch && notSelected;
    });
  };

  const getProductImage = (product) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const storageBaseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
    
    const buildUrl = (imagePath) => {
      if (!imagePath) return null;
      if (imagePath.startsWith('http')) return imagePath;
      if (imagePath.startsWith('/storage/')) return `${storageBaseUrl}${imagePath}`;
      if (imagePath.startsWith('storage/')) return `${storageBaseUrl}/${imagePath}`;
      return `${storageBaseUrl}/storage/${imagePath}`;
    };
    
    if (product.main_image && product.main_image.image_path) {
      return buildUrl(product.main_image.image_path);
    } else if (product.images && product.images.length > 0) {
      const mainImg = product.images.find(img => img.is_main) || product.images[0];
      if (mainImg && mainImg.image_path) {
        return buildUrl(mainImg.image_path);
      }
    }
    return '/images/placeholder-product.jpg';
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filteredProducts = getFilteredProducts();
      if (filteredProducts.length > 0) {
        handleProductSelect(filteredProducts[0].id);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSearchTerm('');
    }
  };

  const handleProductRemove = (productId) => {
    setPackData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.id !== productId)
    }));
  };

  const handleProductQuantityChange = (productId, quantity) => {
    setPackData(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(quantity) || 1 } : p
      )
    }));
  };

  const calculateTotalPrice = () => {
    return packData.products.reduce((total, packProduct) => {
      const product = availableProducts.find(p => p.id === packProduct.id);
      return total + (product ? parseFloat(product.price) * packProduct.quantity : 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const totalPrice = packData.price || calculateTotalPrice();

      const submitData = {
        ...packData,
        price: totalPrice,
        products: packData.products
      };

      await packRepository.update(id, submitData);
      navigate('/admin/packs/pack-list', { 
        replace: true,
        state: { refresh: Date.now() }
      });
    } catch (err) {
      console.error('Error updating pack:', err);
      if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat().join(', ');
        setError(`Erreurs de validation: ${validationErrors}`);
      } else {
        setError(err.response?.data?.message || 'Erreur lors de la modification du pack');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/packs/pack-list');
  };

  if (fetching) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement du pack...</p>
      </div>
    );
  }

  return (
    <div className="pack-edit-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle as="h6" className="mb-0">Modifier le Pack: {packData.name}</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Nom du Pack <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    placeholder="Nom du pack"
                    value={packData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="type" className="form-label">
                    Type de Pack <span className="text-muted">(Sous-titre)</span>
                  </label>
                  <input
                    type="text"
                    id="type"
                    name="type"
                    className="form-control"
                    placeholder="Ex: Pack Premium, Pack Tradition, Pack Mariage..."
                    value={packData.type}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="price" className="form-label">
                    Prix du Pack (DH) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    className="form-control"
                    placeholder="Prix"
                    value={packData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                  <small className="text-muted">
                    Prix calculé automatiquement: {calculateTotalPrice().toFixed(2)} DH
                  </small>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="status" className="form-label">
                    Statut <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    id="status"
                    name="status"
                    value={packData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="quantity" className="form-label">
                    Quantité en Stock <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    className="form-control"
                    placeholder="Quantité"
                    value={packData.quantity}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control"
                    placeholder="Description du pack"
                    value={packData.description}
                    onChange={handleInputChange}
                    rows={3}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col lg={12}>
                <Card className="border">
                  <CardHeader>
                    <CardTitle as="h6" className="mb-0">Produits du Pack</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <label className="form-label">Produits <span className="text-danger">*</span></label>
                      
                      {/* Tags Input with Search */}
                      <div className="position-relative">
                        <div 
                          className="form-control d-flex flex-wrap align-items-center gap-2 p-2" 
                          style={{ 
                            minHeight: '60px',
                            cursor: 'text'
                          }}
                          onClick={(e) => {
                            if (e.target === e.currentTarget || e.target.tagName === 'INPUT') {
                              e.currentTarget.querySelector('input')?.focus();
                            }
                          }}
                        >
                          {/* Selected Products as Tags */}
                          {packData.products.map((packProduct) => {
                            const product = availableProducts.find(p => parseInt(p.id) === parseInt(packProduct.id));
                            if (!product) return null;
                            const productImage = getProductImage(product);
                            return (
                              <div
                                key={packProduct.id}
                                className="d-flex align-items-center gap-2 bg-primary text-white border rounded px-2 py-1"
                                style={{ fontSize: '13px', borderRadius: '4px' }}
                              >
                                <img 
                                  src={productImage} 
                                  alt={product.name}
                                  style={{ width: '20px', height: '20px', objectFit: 'cover', borderRadius: '2px' }}
                                />
                                <span className="fw-bold">{product.name}</span>
                                <span className="badge bg-light text-dark ms-1">x{packProduct.quantity}</span>
                                <button
                                  type="button"
                                  className="btn-close btn-close-white ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductRemove(packProduct.id);
                                  }}
                                  style={{ fontSize: '10px' }}
                                  aria-label="Remove"
                                />
                              </div>
                            );
                          })}
                          
                          {/* Search Input */}
                          <input
                            type="text"
                            className="border-0 flex-grow-1"
                            placeholder={packData.products.length === 0 ? "Rechercher un produit... (Cliquez pour voir tous les produits)" : ""}
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setShowSuggestions(true);
                            }}
                            onKeyDown={handleSearchKeyDown}
                            onFocus={() => setShowSuggestions(true)}
                            onClick={() => setShowSuggestions(true)}
                            style={{ 
                              outline: 'none',
                              minWidth: '200px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        {/* Search Suggestions Dropdown */}
                        {showSuggestions && (() => {
                          const filteredProducts = getFilteredProducts();
                          
                          if (filteredProducts.length > 0) {
                            return (
                              <div 
                                className="border rounded mt-1 bg-white shadow-sm"
                                style={{ 
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  position: 'absolute',
                                  zIndex: 1000,
                                  width: '100%'
                                }}
                              >
                                {filteredProducts.slice(0, 10).map((product) => {
                                  const productImage = getProductImage(product);
                                  return (
                                    <div
                                      key={product.id}
                                      className="d-flex align-items-center gap-2 p-2 border-bottom cursor-pointer"
                                      onClick={() => handleProductSelect(product.id)}
                                      style={{ 
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    >
                                      <img 
                                        src={productImage} 
                                        alt={product.name}
                                        style={{ width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px' }}
                                      />
                                      <div>
                                        <div className="fw-bold">{product.name}</div>
                                        <small className="text-muted">{product.price} DH</small>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          
                          if (searchTerm.trim()) {
                            return (
                              <div 
                                className="border rounded mt-1 bg-white shadow-sm p-2"
                                style={{ 
                                  position: 'absolute',
                                  zIndex: 1000,
                                  width: '100%'
                                }}
                              >
                                <div className="text-muted text-center">Aucun produit trouvé</div>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      
                      {/* Quantity Editor for Selected Products */}
                      {packData.products.length > 0 && (
                        <div className="mt-3">
                          <label className="form-label">Modifier les Quantités</label>
                          <div className="row">
                            {packData.products.map((packProduct) => {
                              const product = availableProducts.find(p => parseInt(p.id) === parseInt(packProduct.id));
                              if (!product) return null;
                              const productImage = getProductImage(product);
                              return (
                                <div key={packProduct.id} className="col-md-6 mb-2">
                                  <div className="d-flex align-items-center gap-2 p-2 border rounded bg-light">
                                    <img 
                                      src={productImage} 
                                      alt={product.name}
                                      style={{ width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                    <div className="flex-grow-1">
                                      <small className="fw-bold">{product.name}</small>
                                    </div>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={packProduct.quantity}
                                      onChange={(e) => handleProductQuantityChange(packProduct.id, e.target.value)}
                                      min="1"
                                      style={{ width: '60px' }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button
                    variant="dark"
                    className="w-100"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button
                    variant="primary"
                    className="w-100"
                    type="submit"
                    disabled={loading || packData.products.length === 0}
                    style={{ background: 'linear-gradient(135deg, #c5a05e 0%, #b8934a 100%)', border: 'none' }}
                  >
                    {loading ? 'Modification...' : 'Modifier le Pack'}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default PackEdit;
